import React, { useEffect, useState } from 'react';
import { Sparkles, MapPin, UserPlus, GraduationCap } from 'lucide-react';
import { C, F } from '../landingTheme';
import { PlanCard, EnterpriseCard } from './PlanCards';
import PricingFaq from './PricingFaq';
import { inr } from './pricingCopy';
import { EnterpriseEnquiryModal } from '../../../shell/EnterpriseEnquiryModal';

function CycleToggle({ cycles, cycle, setCycle }) {
  return (
    <div className="mt-10 flex justify-center">
      <div className="flex w-full sm:w-auto sm:inline-flex gap-1 rounded-2xl sm:rounded-full border bg-white p-1.5" style={{ borderColor: C.border }} role="tablist" data-testid="pricing-cycle-toggle">
        {cycles.map((c) => {
          const on = cycle === c.code;
          return (
            <button key={c.code} role="tab" aria-selected={on} onClick={() => setCycle(c.code)} data-testid={`landing-cycle-${c.code}`}
              className="flex-1 sm:flex-none min-w-0 flex flex-col sm:flex-row items-center justify-center rounded-xl sm:rounded-full px-2 sm:px-5 py-2 text-[13px] sm:text-sm font-semibold transition-colors duration-200"
              style={{ background: on ? C.ink : 'transparent', color: on ? 'white' : C.ink2, fontFamily: F.body }}>
              {c.label}
              {c.discount_percent > 0 && (
                <span className="sm:ml-1.5 text-[11px] font-bold" style={{ color: on ? '#86EFAC' : C.emerald }}>(Save {c.discount_percent}%)</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FoundingBanner({ f }) {
  return (
    <div data-testid="landing-founding-banner"
      className="mt-8 mx-auto max-w-3xl flex items-start sm:items-center gap-3 rounded-2xl border px-5 py-4"
      style={{ background: C.saffronTint, borderColor: '#F6C9B8', fontFamily: F.body }}>
      <Sparkles className="w-5 h-5 shrink-0 mt-0.5 sm:mt-0" style={{ color: C.saffron }} strokeWidth={2.5} />
      <p className="text-sm" style={{ color: C.ink }}>
        <b style={{ color: C.saffron }}>Founding Clinic Offer</b> — {f.discount_percent}% off annual, price locked for {f.locked_renewals} renewals.{' '}
        <b>Only <span data-testid="landing-founding-spots">{f.spots_left} of {f.limit}</span> spots left.</b>
      </p>
    </div>
  );
}

function AddonsRow({ addons, ob }) {
  const loc = addons.extra_location;
  const user = addons.extra_user;
  const free = (ob?.waived_cycles || []).includes('annual');
  const items = [
    loc && { Icon: MapPin, k: 'location', t: 'Extra location', v: `${inr(loc.monthly_price)}/month`, s: `includes ${loc.includes_users} users` },
    user && { Icon: UserPlus, k: 'user', t: 'Extra user', v: `${inr(user.monthly_price)}/month` },
    ob?.amount && { Icon: GraduationCap, k: 'onboarding', t: 'Onboarding & training', v: `${inr(ob.amount)} one-time`, s: free ? 'FREE on annual plans' : null },
  ].filter(Boolean);
  return (
    <div className="mt-10" data-testid="landing-addons-row">
      <p className="text-center text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ fontFamily: F.mono, color: C.ink2 }}>Add-ons</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map(({ Icon, k, t, v, s }) => (
          <div key={k} className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3" style={{ borderColor: C.border }} data-testid={`landing-addon-${k}`}>
            <Icon className="w-5 h-5 shrink-0" style={{ color: C.saffron }} strokeWidth={2} />
            <div className="text-sm leading-tight" style={{ fontFamily: F.body }}>
              <div style={{ color: C.ink2 }}>{t}</div>
              <div className="font-semibold" style={{ color: C.ink }}>{v}
                {s && <span className="ml-1 font-semibold" style={{ color: k === 'onboarding' ? C.emerald : C.ink2 }}>— {s}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PricingSection({ data }) {
  const cycles = data?.billing_cycles || [];
  const [cycle, setCycle] = useState('annual');
  const [enqOpen, setEnqOpen] = useState(false);
  useEffect(() => {
    if (cycles.length && !cycles.some((c) => c.code === cycle)) setCycle(cycles[cycles.length - 1].code);
  }, [cycles, cycle]);

  const plans = data?.plans || [];
  const founding = data?.founding;
  const months = (cycles.find((c) => c.code === cycle) || {}).months || 1;
  const chain = plans.find((p) => p.code === 'chain');

  return (
    <section id="pricing" className="relative py-24 overflow-hidden" style={{ background: C.bone }} data-testid="landing-pricing">
      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest"
            style={{ background: C.saffronTint, color: C.saffron, fontFamily: F.mono }}>Pricing</span>
          <h2 className="mt-4 text-4xl md:text-5xl font-extrabold" style={{ fontFamily: F.display, color: C.ink, letterSpacing: '-0.035em', lineHeight: 1.05 }}>
            One price. <span style={{ color: C.saffron }}>Every feature.</span>
          </h2>
          <p className="mt-4 text-base" style={{ fontFamily: F.body, color: C.ink2 }}>
            Start with a {data?.trial_days || 30}-day free trial. No credit card required.
          </p>
        </div>

        {!data ? (
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="pricing-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-[560px] rounded-3xl border bg-white animate-pulse" style={{ borderColor: C.border }} />)}
          </div>
        ) : (
          <>
            <CycleToggle cycles={cycles} cycle={cycle} setCycle={setCycle} />
            {founding?.available && <FoundingBanner f={founding} />}
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch" data-testid="pricing-cards">
              {plans.map((p) => <PlanCard key={p.code} plan={p} cycle={cycle} months={months} founding={founding} />)}
              {data.enterprise && <EnterpriseCard minLocations={(chain?.included_locations || 3) + 1} onTalk={() => setEnqOpen(true)} />}
            </div>
            <AddonsRow addons={data.addons || {}} ob={data.onboarding_fee} />
            <p className="mt-6 text-center text-xs" style={{ fontFamily: F.body, color: C.ink2 }} data-testid="landing-gst-note">
              All prices exclude {data.gst_percent}% GST. GST invoice provided.
            </p>
            <PricingFaq data={data} />
          </>
        )}
      </div>
      <EnterpriseEnquiryModal open={enqOpen} onClose={() => setEnqOpen(false)} source="landing" />
    </section>
  );
}
